import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/mocks/categories';

const VALUE_ICONS = ['ri-table-line', 'ri-user-search-line', 'ri-star-line', 'ri-shield-check-line'];
const VALUE_IMAGES = [
  'https://readdy.ai/api/search-image?query=A%20minimalist%20desk%20scene%20with%20a%20laptop%20showing%20an%20organized%20spreadsheet%20grid%20and%20a%20small%20stack%20of%20folded%20clothing%20beside%20it%2C%20warm%20natural%20window%20light%2C%20clean%20editorial%20styling%2C%20soft%20earthy%20tones%2C%20premium%20lifestyle%20product%20photography&width=600&height=400&seq=value-spreadsheet&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20resting%20on%20a%20tablet%20showing%20a%20fashion%20catalog%20grid%2C%20soft%20bright%20daylight%2C%20clean%20minimal%20editorial%20scene%2C%20warm%20neutral%20tones%2C%20premium%20search%20concept%20product%20photography&width=600&height=400&seq=value-search&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20collection%20of%20fashion%20products%20with%20five%20star%20rating%20symbols%20floating%20above%20them%2C%20soft%20studio%20lighting%2C%20clean%20editorial%20composition%2C%20warm%20neutral%20tones%2C%20premium%20review%20concept%20product%20photography&width=600&height=400&seq=value-reviews&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20shield%20and%20padlock%20symbol%20resting%20beside%20a%20smartphone%20and%20shopping%20bags%20on%20a%20light%20surface%2C%20soft%20reassuring%20lighting%2C%20clean%20minimal%20editorial%20composition%2C%20warm%20safe%20tones%2C%20premium%20security%20concept%20photography&width=600&height=400&seq=value-safety&orientation=landscape',
];
const INTENT_ICONS = [
  'ri-message-3-line',
  'ri-truck-line',
  'ri-coupon-3-line',
  'ri-price-tag-3-line',
  'ri-archive-line',
  'ri-verified-badge-line',
];

export default function ContentSections() {
  const { t } = useTranslation();

  const valueCards = t('valueProp.cards', { returnObjects: true }) as unknown as {
    title: string;
    text: string;
  }[];

  const tableHeaders = t('core.tableHeaders', { returnObjects: true }) as unknown as string[];
  const tableRows = t('core.tableRows', { returnObjects: true }) as unknown as string[][];

  const intentCards = t('intent.cards', { returnObjects: true }) as unknown as string[];

  const compHeaders = t('comparison.headers', { returnObjects: true }) as unknown as string[];
  const compRows = t('comparison.rows', { returnObjects: true }) as unknown as string[][];

  return (
    <>
      {/* Value proposition */}
      <section className="bg-cream py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-2xl font-bold text-stone-900 md:text-3xl">
              {t('valueProp.title')}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {valueCards.map((card, idx) => (
              <div key={card.title} className="overflow-hidden rounded-lg border border-stone-100 bg-white transition-all hover:-translate-y-1 hover:border-stone-200">
                <div className="relative h-36 w-full overflow-hidden md:h-40">
                  <img
                    src={VALUE_IMAGES[idx]}
                    alt={card.title}
                    title={card.title}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                    <i className={`${VALUE_ICONS[idx] ?? 'ri-file-list-3-line'} text-lg`}></i>
                  </span>
                  <h3 className="mt-3 text-base font-semibold text-stone-900">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-500">{card.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spreadsheet core */}
      <section id="spreadsheet" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl font-bold text-stone-900 md:text-3xl">
                {t('core.title')}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-stone-600 md:text-base">
                {t('core.copy')}
              </p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-6 inline-flex items-center whitespace-nowrap rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                {t('core.cta')}
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>

            <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
                      {tableHeaders.map((h) => (
                        <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            className={`whitespace-nowrap px-4 py-3 ${
                              j === 0 ? 'font-medium text-stone-800' : 'text-stone-600'
                            }`}
                          >
                            {j === 2 ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                {cell} <i className="ri-arrow-right-up-line"></i>
                              </span>
                            ) : j === 3 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                {cell}
                              </span>
                            ) : (
                              cell
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular search intent */}
      <section id="guides" className="bg-cream py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-2xl font-bold text-stone-900 md:text-3xl">
              {t('intent.title')}
            </h2>
            <p className="mt-3 text-sm text-stone-500 md:text-base">{t('intent.subtitle')}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {intentCards.map((card, idx) => (
              <div
                key={card}
                className="flex items-center gap-3 rounded-lg border border-stone-100 bg-white px-5 py-4 transition-colors hover:border-stone-200"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                  <i className={`${INTENT_ICONS[idx] ?? 'ri-question-line'} text-lg`}></i>
                </span>
                <span className="text-sm font-semibold text-stone-800">{card}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold text-stone-900 md:text-3xl">
              {t('comparison.title')}
            </h2>
            <p className="mt-3 text-sm text-stone-500 md:text-base">{t('comparison.subtitle')}</p>
          </div>
          <div className="mt-8 overflow-hidden rounded-lg border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
                    {compHeaders.map((h) => (
                      <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compRows.map((row, i) => (
                    <tr key={i} className="border-t border-stone-100">
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`whitespace-nowrap px-4 py-3 ${
                            j === 0 ? 'font-medium text-stone-900' : 'text-stone-600'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section id="safety" className="bg-sage py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center md:px-6">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-emerald-700">
            <i className="ri-shield-check-line text-2xl"></i>
          </span>
          <h2 className="mt-6 font-display text-2xl font-bold text-stone-900 md:text-3xl">
            {t('safety.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-stone-600 md:text-base">
            {t('safety.copy')}
          </p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-6 inline-flex items-center whitespace-nowrap rounded-md bg-stone-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-stone-800"
          >
            {t('safety.cta')}
          </a>
        </div>
      </section>
    </>
  );
}